import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Col, Descriptions, Row, Spin, Table, Tag, Timeline, Typography } from "antd";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import VuiFormItemView from "../../../components/form-item-view";
import VuiPageTitle from "../../../components/page-title";
import { LEAD_STATUS_COLOR } from "../../../constants";
import { useBreadcrumb } from "../../../context/breadcrumb";
import { BreadcrumbActionType } from "../../../context/breadcrumb/reducer";
import ModalFormActivity from "../components/modal-form-activity";
import LeadActivityTimeline from "../../../components/lead-activity-timeline";
import { useFormLead } from "../hooks/use-form-lead";
import { useGetLead } from "../hooks/use-get-lead";
import { ActivityFormData, ActivityPayloadType } from "./interface";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { openNotification, snakeCaseToTitleCase } from "../../../utils/helpers";
import Space from "antd/es/space";
import { ColumnsType } from "antd/lib/table";
import { LeadActivity } from "../../../models/lead";

const { Text, Title } = Typography;

const LeadDetailModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { loadDetailData, isOnFetchingDetailData, detailData } = useGetLead();
  const title = `${t("common.text.leads")} | `;
  const {
    activityLoading,
    onActivity,
    onDelete,
    deleteLoading,
    onDeleteActivity,
    activityDeleteLoading,
  } = useFormLead();
  const { dispatch } = useBreadcrumb();
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showDeleteActivityModal, setShowDeleteActivityModal] =
    useState<boolean>(false);
  const [deleteActivityId, setDeleteActivityId] = useState<string | null>(null);

  const name = useMemo(() => {
    return detailData?.lead_name || detailData?.customer?.name || "-";
  }, [detailData]);

  const fullName = useMemo(() => {
    if (detailData?.existing_client) {
      return detailData?.customer?.name || "-";
    }

    const leadPrefix =
      detailData?.type?.label !== "None" ? detailData?.type?.label : "";

    return detailData?.lead_name
      ? `${leadPrefix} ${detailData?.lead_name}`
      : "-";
  }, [detailData]);

  const emails = useMemo(() => {
    return detailData?.emails?.map((contact) => contact).join(", ");
  }, [detailData]);

  const phones = useMemo(() => {
    return detailData?.phones?.map((contact) => contact).join(", ");
  }, [detailData]);

  const products = useMemo(() => {
    return detailData?.products?.map((product) => product.name).join(", ");
  }, [detailData]);

  useEffect(() => {
    dispatch({
      type: BreadcrumbActionType.Update,
      payload: [
        {
          link: "/",
          title: t("common.text.home"),
        },
        {
          link: "/lead",
          title: t("common.text.lead"),
        },
        {
          link: `/lead/${id}`,
          title: name || t("common.text.detail"),
          isDisabled: true,
        },
      ],
    });
  }, [dispatch, t, id]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadDetailData(id, {
          with: [
            "customer",
            "employeeSize",
            "type",
            "industry",
            "priority",
            "assign",
            "products",
            "activities.timeline",
            "activities.internalInfluence",
            "activities.proposalAttachment",
            "activities.contractAttachment",
          ],
        });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleEdit = () => {
    navigate(`/lead/${id}/edit`);
  };

  const handleActivity = async (formData: ActivityFormData) => {
    const normalizeData: ActivityPayloadType = {
      ...formData,
      status: formData.status?.value as string,
      date: moment(formData.date).format("YYYY-MM-DD HH:mm:ss"),
      timeline_id: formData.timeline?.value as number,
      estimated_close_date: formData.estimated_close_date
        ? moment(formData.estimated_close_date).format("YYYY-MM-DD HH:mm:ss")
        : undefined,
      internal_influence_id: formData.internal_influence_id?.value as number,
      contract_attachment_id:
        formData.contract_attachment_id &&
          formData.contract_attachment_id.length
          ? (formData.contract_attachment_id[0].id as number)
          : undefined,
      proposal_attachment_id:
        formData.proposal_attachment_id &&
          formData.proposal_attachment_id.length
          ? (formData.proposal_attachment_id[0].id as number)
          : undefined,
      lost_reason: formData.lost_reason?.value as string,
    };

    if (id) {
      await onActivity(id, normalizeData);
      setShowActivityForm(false);
      await loadDetailData(id, {
        with: [
          "customer",
          "employeeSize",
          "type",
          "industry",
          "priority",
          "assign",
          "products",
          "activities.timeline",
          "activities.internalInfluence",
          "activities.proposalAttachment",
          "activities.contractAttachment",
        ],
      });
    }
  };

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleCloseModalDeleteActivity = useCallback(() => {
    setShowDeleteActivityModal(false);
    setDeleteActivityId(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.leads"),
          })
        );

        navigate(`/lead`);
      });
    }
  }, [id, navigate, onDelete, t]);

  const handleConfirmDeleteActivity = useCallback((): void => {
    if (id) {
      onDeleteActivity(id, deleteActivityId ?? "").then(() => {
        setShowDeleteActivityModal(false);
        setDeleteActivityId(null);

        loadDetailData(id, {
          with: [
            "customer",
            "employeeSize",
            "type",
            "industry",
            "priority",
            "assign",
            "products",
            "activities.timeline",
            "activities.internalInfluence",
            "activities.proposalAttachment",
            "activities.contractAttachment",
          ],
        }).catch();
      });
    }
  }, [id, navigate, onDeleteActivity, deleteActivityId, t]);

  // Descriptions rendering moved to src/components/TimelineTable/TimelineTable.tsx

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <VuiPageTitle
            title={
              <Title level={4} style={{ fontWeight: 500, marginBottom: 0 }}>
                {title}
                <Text style={{ fontWeight: 800 }}>{name}</Text>
              </Title>
            }
            onBackLink="/lead"
            rightTitleContent={[
              detailData?.code && (
                <Tag>{detailData?.code}</Tag>
              ),
              <Tag
                color={_.get(
                  LEAD_STATUS_COLOR,
                  detailData?.status ?? "",
                  "blue"
                )}
              >
                {snakeCaseToTitleCase(detailData?.status ?? " ")}
              </Tag>,
            ]}
          >
            <VuiAccessible access="lead.destroy">
              {detailData?.can_delete && (
                <Button
                  size="large"
                  type="primary"
                  ghost
                  className="btn-gray"
                  icon={<DeleteOutlined />}
                  onClick={() => setShowDeleteModal(true)}
                />
              )}
            </VuiAccessible>

            <VuiAccessible access="lead.update">
              {detailData?.can_update && (
                <Button
                  size="large"
                  type="primary"
                  ghost
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                />
              )}
            </VuiAccessible>

            {detailData?.can_activity && (
              <Button
                size="large"
                type="primary"
                ghost
                onClick={() => setShowActivityForm(true)}
              >
                {t("common.text.updateActivity")}
              </Button>
            )}
          </VuiPageTitle>
        </Col>
      </Row>

      <Spin size="large" spinning={isOnFetchingDetailData}>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card>
              <Row gutter={[48, 16]}>
                <Col xs={24} md={8}>
                  <Row gutter={[16, 16]}>
                    {detailData?.existing_client && (
                      <>
                        <Col xs={24} md={24}>
                          <VuiFormItemView
                            label={t("common.form.client.label")}
                            value={fullName}
                          />
                        </Col>
                      </>
                    )}
                    {!detailData?.existing_client && (
                      <>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.leadName.label")}
                            value={fullName}
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <VuiFormItemView
                            label={t("common.form.employeeSize.label")}
                            value={detailData?.employee_size?.label}
                          />
                        </Col>
                        <Col xs={24} md={12}>
                          <VuiFormItemView
                            label={t("common.form.industry.label")}
                            value={detailData?.industry?.name}
                          />
                        </Col>
                        <Col xs={24}>
                          <VuiFormItemView
                            label={t("common.form.address.label")}
                            value={detailData?.address || "-"}
                          />
                        </Col>
                      </>
                    )}
                  </Row>
                </Col>
                <Col xs={24} md={16}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.date.label")}
                        value={moment(detailData?.date).format("DD MMM YYYY")}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.priority.label")}
                        value={detailData?.priority?.label}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.assignee.label")}
                        value={
                          detailData?.assign
                            ? `${detailData.assign.name || ""} (${detailData.assign.initial || ""
                            })`
                            : ""
                        }
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.emails.label")}
                        value={emails ?? "-"}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.phones.label")}
                        value={phones ?? "-"}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.potentialServices.label")}
                        value={`${products}`}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.fullName.label")}
                        value={detailData?.full_name || "-"}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <VuiFormItemView
                        label={t("common.form.jobTitle.label")}
                        value={detailData?.job_title || "-"}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24}>
            <Card>
              <Timeline>
                {detailData?.activities
                  ?.slice()
                  .reverse()
                  .map((activity, idx) => {
                    const statusLabel = snakeCaseToTitleCase(activity.status);
                    const dateLabel = moment(activity.date).format("DD MMM YYYY, HH:mm");
                    const sentence = t("common.text.statusUpdatedTo", {
                      status: statusLabel,
                      date: dateLabel,
                    });
                    const parts = sentence.split(statusLabel);

                    return (
                      <Timeline.Item
                        key={activity.id}
                        color={_.get(LEAD_STATUS_COLOR, activity.status, "blue")}
                      >
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Text>
                            {parts[0]}
                            <Text strong>{statusLabel}</Text>
                            {parts[1]}
                          </Text>

                          <LeadActivityTimeline activity={activity} />

                          {(idx === 0 && activity.status !== "PROSPECT") && (
                            <VuiAccessible access="lead.activity-delete">
                              <Button
                                type="default"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  setDeleteActivityId(String(activity.id));
                                  setShowDeleteActivityModal(true);
                                }}
                              />
                            </VuiAccessible>
                          )}
                        </Space>
                      </Timeline.Item>
                    );
                  })}

                {!["WON", "LOST"].includes(detailData?.status ?? "") &&
                  detailData?.activities?.length === 0 && (
                    <Timeline.Item color="gray" />
                  )}
              </Timeline>
            </Card>
          </Col>
        </Row>
      </Spin>

      {showActivityForm && (
        <ModalFormActivity
          title={
            <>
              <Title
                level={5}
                style={{
                  color: " #979DAD",
                  fontWeight: 400,
                }}
              >
                {t("modal.leadActivityUpdate.title")}
                <br />
                <Title level={4}>{name}</Title>
              </Title>
            </>
          }
          show={showActivityForm}
          onClose={() => setShowActivityForm(false)}
          onSubmit={handleActivity}
          submitLoading={activityLoading}
          availableStatuses={detailData?.available_statuses ?? []}
          assignee={detailData?.assign}
        />
      )}
      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
      <VuiModalConfirmation
        show={showDeleteActivityModal}
        isLoading={activityDeleteLoading}
        onSubmit={handleConfirmDeleteActivity}
        onCancel={handleCloseModalDeleteActivity}
      />
    </>
  );
};

export default LeadDetailModule;
